import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Shield, Wrench, CalendarOff, ArrowRight, Truck, Play, DollarSign, FileText, ClipboardCheck, XCircle, Lock } from "lucide-react";
import type { LoadFailureKind } from "../../lib/load-failure";

export interface OpsAlert {
  label: string;
  detail: string;
  action: string;
  path: string;
  urgent?: boolean;
  /**
   * Set when the card reports a read that did not arrive rather than a
   * condition in the data. It takes the neutral style below, never the hue of
   * the alert it stands in for: a red dollar sign would read as "money is
   * overdue", which is the one thing a failed read cannot know.
   */
  failure?: LoadFailureKind;
  /**
   * The fill, when the caller already knows the severity (the server's Action
   * Queue items carry one). Absent, the label decides, as it always has — so
   * every casing card is drawn exactly as before.
   */
  tone?: OpsAlertTone;
}

export type OpsAlertTone = 'red' | 'amber' | 'blue' | 'neutral';

interface OpsAlertsProps {
  alerts: OpsAlert[];
  /**
   * Drop the "Action Required" heading row. The Overview's Action Queue panel
   * carries its own <h2> and count, and a second heading inside it is noise.
   */
  bare?: boolean;
}

// Same split as LoadFailureNotice: the glyph tells refused from unreachable,
// and neither is an alarm colour. The ink is the fieldiq muted token for the
// detail line's reason: the shadcn muted one measured 2.35:1 on this tile in
// dark, under the 3:1 a meaningful glyph needs. This one is 3.57 there, 4.86
// in light.
const failureStyle: Record<LoadFailureKind, { icon: any; color: string; bg: string }> = {
  refused: { icon: Lock, color: 'text-fieldiq-muted', bg: 'bg-fieldiq-dark' },
  unreachable: { icon: AlertTriangle, color: 'text-fieldiq-muted', bg: 'bg-fieldiq-dark' },
};

/**
 * Saturated signal fills with the one dark ink, the badge idiom. These were
 * 300/400 inks on a 20% wash of the same hue, which in light mode put the glyph
 * at roughly 1.5:1 on its own tile. The signal hues do not change with the
 * theme, so one dark ink holds in both.
 */
const AMBER = { color: 'text-fi-signal-ink', bg: 'bg-fi-signal-amber' };
const RED = { color: 'text-fi-signal-ink', bg: 'bg-fi-signal-red' };
const BLUE = { color: 'text-fi-signal-ink', bg: 'bg-fi-signal-blue' };
const SLATE = { color: 'text-fi-signal-ink', bg: 'bg-fi-signal-slate' };
const TONE_FILL: Record<OpsAlertTone, { color: string; bg: string }> = { red: RED, amber: AMBER, blue: BLUE, neutral: SLATE };

const alertIconMap: Record<string, { icon: any; color: string; bg: string }> = {
  'jobs need': { icon: Play, color: AMBER.color, bg: AMBER.bg },
  'left on site': { icon: Truck, color: AMBER.color, bg: AMBER.bg },
  'equipment cert': { icon: Wrench, color: AMBER.color, bg: AMBER.bg },
  'safety': { icon: Shield, color: RED.color, bg: RED.bg },
  'inspection': { icon: AlertTriangle, color: RED.color, bg: RED.bg },
  'time-off': { icon: CalendarOff, color: BLUE.color, bg: BLUE.bg },
  'invoice': { icon: DollarSign, color: RED.color, bg: RED.bg },
  'quote': { icon: FileText, color: AMBER.color, bg: AMBER.bg },
  'ticket': { icon: FileText, color: AMBER.color, bg: AMBER.bg },
  'rejected': { icon: XCircle, color: RED.color, bg: RED.bg },
  'jsa': { icon: ClipboardCheck, color: AMBER.color, bg: AMBER.bg },
  'incident': { icon: Shield, color: RED.color, bg: RED.bg },
};

function getAlertStyle(label: string) {
  if (label.includes('to be started')) return alertIconMap['jobs need'];
  if (label.includes('left on site')) return alertIconMap['left on site'];
  if (label.includes('equipment cert')) return alertIconMap['equipment cert'];
  if (label.toLowerCase().includes('safety')) return alertIconMap['safety'];
  if (label.includes('inspection')) return alertIconMap['inspection'];
  if (label.includes('time-off')) return alertIconMap['time-off'];
  if (label.includes('invoice')) return alertIconMap['invoice'];
  if (label.includes('quote')) return alertIconMap['quote'];
  if (label.includes('rejected by customer')) return alertIconMap['rejected'];
  if (label.includes('ticket')) return alertIconMap['ticket'];
  if (label.includes('JSA')) return alertIconMap['jsa'];
  if (label.includes('incident')) return alertIconMap['incident'];
  return { icon: AlertTriangle, color: AMBER.color, bg: AMBER.bg };
}

const OpsAlerts = ({ alerts, bare }: OpsAlertsProps) => {
  const navigate = useNavigate();

  if (alerts.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25, ease: "easeOut" }}
    >
      {!bare && (
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-fieldiq-muted" />
          {/* Same token as the detail line, same reason: the shadcn muted one
              read 3.75:1 on the dark page. This is 5.72 there, 5.20 in light. */}
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">Action Required</h3>
          <motion.span
            className="px-1.5 py-0.5 rounded-full bg-fi-signal-amber text-fi-signal-ink text-[10px] font-bold"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            {alerts.length}
          </motion.span>
        </div>
      </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {alerts.map((a, i) => {
          const style = a.failure
            ? failureStyle[a.failure]
            : a.tone
              ? { ...getAlertStyle(a.label), ...TONE_FILL[a.tone] }
              : getAlertStyle(a.label);
          const Icon = style.icon;

          return (
            <motion.div
              key={i}
              role="group"
              aria-label={a.label}
              className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3.5 flex items-center gap-3 hover:bg-fieldiq-dark/70 transition-colors cursor-pointer group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.1, duration: 0.4, ease: "easeOut" }}
              whileHover={{ x: 4 }}
              onClick={() => navigate(a.path)}
            >
              <div data-testid="ops-alert-tile" className={`w-9 h-9 rounded-fi-control ${style.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon data-testid="ops-alert-glyph" className={`w-4 h-4 ${style.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-fieldiq-text">{a.label}</p>
                {/* The detail line's ink is the fieldiq muted token, not the
                    shadcn muted-foreground one. That one resolves to 3.16:1 on
                    the dark card; this one is 4.81 there and 5.99 in light,
                    which is where the shadcn one already stood. */}
                {a.detail && <p data-testid="ops-alert-detail" className="text-[11px] text-fieldiq-muted mt-0.5 truncate">{a.detail}</p>}
              </div>
              <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card transition-colors flex-shrink-0">
                <span className="text-[10px] font-semibold text-fieldiq-text">{a.action}</span>
                <ArrowRight className="w-3 h-3 text-fieldiq-muted transition-colors" />
              </button>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default OpsAlerts;
